import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { FC, useState } from 'react'
import { CalendarEvent } from '../../../../types/calendar'
import { getTextContentFromReplyContent } from '../../../../utils/regularRequests'
import { EventInfoDialog } from './EventInfoDialog'
import { EventInfoPopover } from './EventInfoPopover'

type Props = {
    popoverPosition: 'left' | 'right'
    variant: 'row' | 'column'
    lineClamp?: number
    data: CalendarEvent
    onClick?: VoidFunction
}

export const EventItem: FC<Props> = ({
    popoverPosition,
    variant,
    lineClamp = 3,
    data,
    onClick,
}) => {
    const { breakpoints } = useTheme()

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget)
        onClick?.()
    }
    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const isRegularRequest = data && 'reply' in data

    const displayRow = variant === 'row'

    const description = isRegularRequest
        ? getTextContentFromReplyContent(data.reply.content ?? [])?.text
        : data.title
    const date = isRegularRequest ? data.timing.send_time : data.start_at

    const isOpenDetails = !!anchorEl

    return (
        <>
            {isMobile ? (
                <EventInfoDialog open={isOpenDetails} onClose={handleCloseMenu} data={data} />
            ) : (
                <EventInfoPopover
                    anchorEl={anchorEl}
                    onClose={handleCloseMenu}
                    data={data}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: popoverPosition,
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: popoverPosition === 'left' ? 'right' : 'left',
                    }}
                />
            )}
            <Stack
                onClick={handleOpenMenu}
                direction="row"
                sx={{
                    width: '100%',
                    borderRadius: '4px',
                    p: '4px',
                    cursor: 'pointer',
                    gap: displayRow ? '2px' : '4px',
                    backgroundColor: isOpenDetails ? '#F6F6F6' : 'unset',
                    ':hover': {
                        backgroundColor: '#F6F6F6',
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        width: '3px',
                        minWidth: '3px',
                        height: '100%',
                        backgroundColor: !isRegularRequest ? '#229F0A' : '#006CFD',
                        borderRadius: '12px',
                    }}
                />
                <Stack
                    direction={displayRow ? 'row' : 'column'}
                    gap={displayRow ? '4px' : '2px'}
                    sx={{
                        width: '-webkit-fill-available',
                    }}>
                    <Typography variant="Description" color="#5A5C5E">
                        {format(date, 'HH:mm')}
                    </Typography>
                    <Typography
                        variant="Description"
                        color="#1B1C1D"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: `${lineClamp}`,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {description}
                    </Typography>
                </Stack>
            </Stack>
        </>
    )
}
